import type { ChronicleEntry, WorldState } from "../core/types";
import { isElsewhereLoafPacket, projectElsewhereLoafTransition, type ElsewhereLoafPacket } from "../ui/elsewhere-loaf-view";
import {
  isTownItineraryPacketV1,
  projectTownItinerary,
  type TownItineraryPacketV1,
} from "../ui/town-itinerary";
import {
  isBattleSpoilsComparisonPacketV1,
  projectBattleSpoilsComparison,
  type BattleSpoilsComparisonPacketV1,
} from "../ui/battle-spoils";
import {
  isCompanionFarewellPacket,
  projectCompanionFarewell,
  type CompanionFarewellPacket,
} from "../ui/companion-farewell";
import {
  isHeroLevelUpPacketV1,
  projectHeroLevelUp,
  type HeroLevelUpPacketV1,
} from "../ui/hero-level-up";
import {
  isHeroLevelUpPacketV2,
  projectHeroLevelUpPacketV2,
  type HeroLevelUpPacketV2,
} from "../ui/hero-level-up-presentation";
import {
  isHeroGrowthAllocationPacketV1,
  projectHeroGrowthAllocation,
  type HeroGrowthAllocationPacketV1,
} from "../ui/hero-growth-allocation";
import {
  isAbilityResonancePacketV1,
  projectAbilityResonance,
  type AbilityResonancePacketV1,
} from "../ui/ability-resonance";
import {
  isTrapResolutionPacket,
  projectTrapResolution,
  type TrapResolutionPacket,
} from "../ui/trap-resolution";
import {
  isWeaponMemoryCeremonyPacketV1,
  projectWeaponMemoryCeremony,
  type WeaponMemoryCeremonyPacketV1,
} from "../ui/weapon-memory";
import {
  isFieldNoteResolutionPacketV1,
  projectFieldNoteResolution,
  type FieldNoteResolutionPacketV1,
} from "../ui/field-note-resolution";
import {
  isFieldNoteResolutionPacketV2,
  projectFieldNoteResolutionPacketV2,
  type FieldNoteResolutionPacketV2,
} from "../ui/field-note-resolution-presentation";

export const cutawayRegistryVersion = 1 as const;
export const cutawayQueueCapacity = 2 as const;
export const cutawayRecipeLimit = 16 as const;
export const cutawayPhaseLimit = 8 as const;
export const cutawayFlavorLimit = 8 as const;

export type ProductionCutawayRecipeKey =
  | "elsewhere-loaf@1"
  | "trap-resolution@1"
  | "companion-farewell@1"
  | "hero-level-up@1"
  | "hero-level-up@2"
  | "hero-growth-allocation@1"
  | "field-note-resolution@1"
  | "field-note-resolution@2"
  | "ability-resonance@1"
  | "weapon-memory@1"
  | "battle-spoils@1"
  | "town-itinerary@1";
export type CutawayPresentationMode = "full" | "reduced" | "still";
export type CutawayResolutionReason =
  | "registered"
  | "unknown-registry-version"
  | "unknown-recipe"
  | "packet-version-mismatch"
  | "invalid-packet-envelope";

export interface CutawayPacketEnvelope {
  readonly schemaVersion: number;
  readonly eventId: string;
  readonly tick: number;
}

export interface CutawayStaticEnvelopeV1 {
  readonly schemaVersion: 1;
  readonly eventId: string;
  readonly tick: number;
  readonly location: string;
  readonly headline: string;
  readonly action: string;
  readonly consequence: string;
}

export interface CutawayDurationBudget {
  readonly targetMs: number;
  readonly maximumMs: number;
  readonly staticHoldMs: number;
}

export interface CutawayEffectBudget {
  readonly movingActors: number;
  readonly cameraShots: number;
  readonly flavorLayers: number;
}

export interface CutawayRecipeV1 {
  readonly registryVersion: 1;
  readonly key: string;
  readonly packetSchemaVersion: number;
  readonly phaseOrder: readonly string[];
  readonly terminalPhase: string;
  readonly actorRequirements: readonly string[];
  readonly propRequirements: readonly string[];
  readonly truthCueIds: readonly string[];
  readonly allowedFlavorIds: readonly string[];
  readonly durationBudget: CutawayDurationBudget;
  readonly effectBudget: CutawayEffectBudget;
  readonly terminalTableau: string;
  readonly domEquivalentId: string;
  readonly reducedMotion: "complete-static-tableau";
  readonly repetitionFingerprintVersion: number | null;
  readonly repetitionFingerprintFields: readonly string[];
}

export interface CutawayRegistryV1 {
  readonly schemaVersion: 1;
  readonly recipes: readonly CutawayRecipeV1[];
}

export interface CutawayAdapterManifestEntry {
  readonly recipeKey: string;
  readonly domEquivalentId: string;
  readonly truthCueIds: readonly string[];
}

export interface CutawayCandidate<
  Key extends string = string,
  Packet extends CutawayPacketEnvelope = CutawayPacketEnvelope,
> {
  readonly registryVersion: 1;
  readonly recipeKey: Key;
  readonly eventId: string;
  readonly packet: Packet;
  readonly staticEnvelope: CutawayStaticEnvelopeV1;
}

export type TrapCutawayCandidate = CutawayCandidate<"trap-resolution@1", TrapResolutionPacket>;
export type FarewellCutawayCandidate = CutawayCandidate<"companion-farewell@1", CompanionFarewellPacket>;
export type HeroLevelUpCutawayCandidate = CutawayCandidate<"hero-level-up@1", HeroLevelUpPacketV1>;
export type HeroLevelUpChampionCutawayCandidate = CutawayCandidate<"hero-level-up@2", HeroLevelUpPacketV2>;
export type HeroGrowthAllocationCutawayCandidate = CutawayCandidate<"hero-growth-allocation@1", HeroGrowthAllocationPacketV1>;
export type FieldNoteResolutionCutawayCandidate = CutawayCandidate<"field-note-resolution@1", FieldNoteResolutionPacketV1>;
export type FieldNoteResolutionLiveTellCutawayCandidate = CutawayCandidate<"field-note-resolution@2", FieldNoteResolutionPacketV2>;
export type AbilityResonanceCutawayCandidate = CutawayCandidate<"ability-resonance@1", AbilityResonancePacketV1>;
export type WeaponMemoryCutawayCandidate = CutawayCandidate<"weapon-memory@1", WeaponMemoryCeremonyPacketV1>;
export type BattleSpoilsCutawayCandidate = CutawayCandidate<"battle-spoils@1", BattleSpoilsComparisonPacketV1>;
export type TownItineraryCutawayCandidate = CutawayCandidate<"town-itinerary@1", TownItineraryPacketV1>;
export type ElsewhereLoafCutawayCandidate = CutawayCandidate<"elsewhere-loaf@1", ElsewhereLoafPacket>;
export type ProductionCutawayCandidate =
  | ElsewhereLoafCutawayCandidate
  | TrapCutawayCandidate
  | FarewellCutawayCandidate
  | HeroLevelUpCutawayCandidate
  | HeroLevelUpChampionCutawayCandidate
  | HeroGrowthAllocationCutawayCandidate
  | FieldNoteResolutionCutawayCandidate
  | FieldNoteResolutionLiveTellCutawayCandidate
  | AbilityResonanceCutawayCandidate
  | WeaponMemoryCutawayCandidate
  | BattleSpoilsCutawayCandidate
  | TownItineraryCutawayCandidate;
export type AnyCutawayCandidate = CutawayCandidate<string, CutawayPacketEnvelope>;

export interface CutawayResolution {
  readonly mode: "animate" | "static-chronicle";
  readonly reason: CutawayResolutionReason;
  readonly recipe: CutawayRecipeV1 | null;
  readonly staticEnvelope: CutawayStaticEnvelopeV1 | null;
}

export interface CutawayQueue {
  readonly active: AnyCutawayCandidate | null;
  readonly pending: AnyCutawayCandidate | null;
}

export type CutawayQueueAction = "start" | "queued" | "deduplicated" | "dropped" | "fallback";

export interface CutawayQueueResult {
  readonly queue: CutawayQueue;
  readonly action: CutawayQueueAction;
  readonly resolution: CutawayResolution;
}

const recipeKeys = Object.freeze([
  "registryVersion",
  "key",
  "packetSchemaVersion",
  "phaseOrder",
  "terminalPhase",
  "actorRequirements",
  "propRequirements",
  "truthCueIds",
  "allowedFlavorIds",
  "durationBudget",
  "effectBudget",
  "terminalTableau",
  "domEquivalentId",
  "reducedMotion",
  "repetitionFingerprintVersion",
  "repetitionFingerprintFields",
] as const);

function sameKeys(value: object, expected: readonly string[]): boolean {
  const actual = Object.keys(value).sort();
  return actual.length === expected.length
    && actual.every((key, index) => key === [...expected].sort()[index]);
}

function nonEmptyUniqueStrings(values: readonly string[], limit: number): boolean {
  return values.length > 0
    && values.length <= limit
    && values.every((value) => typeof value === "string" && value.trim() === value && value.length > 0)
    && new Set(values).size === values.length;
}

function boundedStrings(values: readonly string[], limit: number): boolean {
  return values.length <= limit
    && values.every((value) => typeof value === "string" && value.trim() === value && value.length > 0)
    && new Set(values).size === values.length;
}

function isPositiveSafeInteger(value: number): boolean {
  return Number.isSafeInteger(value) && value > 0;
}

function isNonNegativeSafeInteger(value: number): boolean {
  return Number.isSafeInteger(value) && value >= 0;
}

function validRecipe(recipe: CutawayRecipeV1): boolean {
  const durationKeys = ["targetMs", "maximumMs", "staticHoldMs"] as const;
  const effectKeys = ["movingActors", "cameraShots", "flavorLayers"] as const;
  return sameKeys(recipe, recipeKeys)
    && recipe.registryVersion === cutawayRegistryVersion
    && typeof recipe.key === "string"
    && /^[a-z][a-z0-9-]*@[1-9][0-9]*$/.test(recipe.key)
    && isPositiveSafeInteger(recipe.packetSchemaVersion)
    && nonEmptyUniqueStrings(recipe.phaseOrder, cutawayPhaseLimit)
    && recipe.phaseOrder.includes(recipe.terminalPhase)
    && nonEmptyUniqueStrings(recipe.actorRequirements, cutawayRecipeLimit)
    && boundedStrings(recipe.propRequirements, cutawayRecipeLimit)
    && nonEmptyUniqueStrings(recipe.truthCueIds, cutawayRecipeLimit)
    && boundedStrings(recipe.allowedFlavorIds, cutawayFlavorLimit)
    && sameKeys(recipe.durationBudget, durationKeys)
    && isPositiveSafeInteger(recipe.durationBudget.targetMs)
    && isPositiveSafeInteger(recipe.durationBudget.maximumMs)
    && recipe.durationBudget.maximumMs >= recipe.durationBudget.targetMs
    && recipe.durationBudget.maximumMs <= 30_000
    && isPositiveSafeInteger(recipe.durationBudget.staticHoldMs)
    && recipe.durationBudget.staticHoldMs <= recipe.durationBudget.targetMs
    && sameKeys(recipe.effectBudget, effectKeys)
    && isNonNegativeSafeInteger(recipe.effectBudget.movingActors)
    && isPositiveSafeInteger(recipe.effectBudget.cameraShots)
    && isNonNegativeSafeInteger(recipe.effectBudget.flavorLayers)
    && typeof recipe.terminalTableau === "string"
    && recipe.terminalTableau.length > 0
    && typeof recipe.domEquivalentId === "string"
    && recipe.domEquivalentId.length > 0
    && recipe.reducedMotion === "complete-static-tableau"
    && (recipe.repetitionFingerprintVersion === null
      ? recipe.repetitionFingerprintFields.length === 0
      : isPositiveSafeInteger(recipe.repetitionFingerprintVersion)
        && nonEmptyUniqueStrings(recipe.repetitionFingerprintFields, cutawayRecipeLimit));
}

function freezeCopy<Value>(value: Value): Value {
  if (Array.isArray(value)) {
    return Object.freeze(value.map((entry) => freezeCopy(entry))) as Value;
  }
  if (typeof value === "object" && value !== null) {
    const copy: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) copy[key] = freezeCopy(entry);
    return Object.freeze(copy) as Value;
  }
  return value;
}

export function createCutawayRegistry(recipes: readonly CutawayRecipeV1[]): CutawayRegistryV1 {
  if (recipes.length === 0 || recipes.length > cutawayRecipeLimit) {
    throw new Error("Cutaway registry size is outside the bounded contract");
  }
  const keys = new Set<string>();
  for (const recipe of recipes) {
    if (!validRecipe(recipe)) throw new Error(`Invalid cutaway recipe: ${recipe.key}`);
    if (keys.has(recipe.key)) throw new Error(`Duplicate cutaway recipe: ${recipe.key}`);
    keys.add(recipe.key);
  }
  return Object.freeze({
    schemaVersion: cutawayRegistryVersion,
    recipes: Object.freeze(recipes.map((recipe) => freezeCopy(recipe))),
  });
}

export function validateCutawayAdapterManifest(
  registry: CutawayRegistryV1,
  manifest: readonly CutawayAdapterManifestEntry[],
): boolean {
  if (manifest.length !== registry.recipes.length) return false;
  const keys = new Set(manifest.map((entry) => entry.recipeKey));
  if (keys.size !== manifest.length) return false;
  return registry.recipes.every((recipe) => {
    const entry = manifest.find((candidate) => candidate.recipeKey === recipe.key);
    if (entry === undefined || entry.domEquivalentId !== recipe.domEquivalentId) return false;
    const expected = [...recipe.truthCueIds].sort();
    const actual = [...entry.truthCueIds].sort();
    return actual.length === expected.length
      && actual.every((truthCueId, index) => truthCueId === expected[index]);
  });
}

const trapRecipe: CutawayRecipeV1 = {
  registryVersion: 1,
  key: "trap-resolution@1",
  packetSchemaVersion: 1,
  phaseOrder: ["command", "inspection", "attempt", "reveal", "consequence", "final"],
  terminalPhase: "final",
  actorRequirements: ["equipped-hero"],
  propRequirements: ["dungeon-mechanism", "check-tableau"],
  truthCueIds: [
    "trap-cutaway-command",
    "trap-cutaway-inspection",
    "trap-cutaway-check",
    "trap-cutaway-result",
    "trap-cutaway-consequence",
    "trap-cutaway-progress",
  ],
  allowedFlavorIds: ["boot-stop", "wire-curl", "rune-wobble"],
  durationBudget: { targetMs: 8_000, maximumMs: 11_000, staticHoldMs: 1_200 },
  effectBudget: { movingActors: 1, cameraShots: 3, flavorLayers: 1 },
  terminalTableau: "resolved-mechanism-with-check-and-consequence",
  domEquivalentId: "trap-cutaway",
  reducedMotion: "complete-static-tableau",
  repetitionFingerprintVersion: 1,
  repetitionFingerprintFields: ["trapKind", "stage", "phaseAfter", "completedExit"],
};

const farewellRecipe: CutawayRecipeV1 = {
  registryVersion: 1,
  key: "companion-farewell@1",
  packetSchemaVersion: 1,
  phaseOrder: ["promise", "journey", "arrival", "farewell", "legacy", "final"],
  terminalPhase: "final",
  actorRequirements: ["equipped-hero", "departing-companion"],
  propRequirements: ["profession-tools", "destination-road"],
  truthCueIds: [
    "farewell-cutaway-promise",
    "farewell-cutaway-journey",
    "farewell-cutaway-arrival",
    "farewell-cutaway-departure",
    "farewell-cutaway-legacy",
    "farewell-cutaway-progress",
  ],
  allowedFlavorIds: [],
  durationBudget: { targetMs: 8_000, maximumMs: 11_000, staticHoldMs: 1_200 },
  effectBudget: { movingActors: 2, cameraShots: 1, flavorLayers: 0 },
  terminalTableau: "hero-witnesses-companion-departure",
  domEquivalentId: "farewell-cutaway",
  reducedMotion: "complete-static-tableau",
  repetitionFingerprintVersion: null,
  repetitionFingerprintFields: [],
};

const heroLevelUpRecipe: CutawayRecipeV1 = {
  registryVersion: 1,
  key: "hero-level-up@1",
  packetSchemaVersion: 1,
  phaseOrder: ["source", "threshold", "ascent", "mechanics", "tableau", "final"],
  terminalPhase: "final",
  actorRequirements: ["equipped-hero"],
  propRequirements: ["earned-thresholds", "mechanical-facts", "final-equipment"],
  truthCueIds: [
    "level-up-cutaway-source",
    "level-up-cutaway-threshold",
    "level-up-cutaway-level",
    "level-up-cutaway-mechanics",
    "level-up-cutaway-tableau",
    "level-up-cutaway-progress",
  ],
  allowedFlavorIds: [],
  durationBudget: { targetMs: 8_000, maximumMs: 11_000, staticHoldMs: 1_200 },
  effectBudget: { movingActors: 1, cameraShots: 2, flavorLayers: 0 },
  terminalTableau: "equipped-hero-with-earned-level-and-mechanical-facts",
  domEquivalentId: "level-up-cutaway",
  reducedMotion: "complete-static-tableau",
  repetitionFingerprintVersion: null,
  repetitionFingerprintFields: [],
};

const heroLevelUpChampionRecipe: CutawayRecipeV1 = {
  ...heroLevelUpRecipe,
  key: "hero-level-up@2",
  packetSchemaVersion: 2,
  propRequirements: [
    "earned-thresholds",
    "mechanical-facts",
    "final-equipment",
    "immutable-earned-hall-seal",
  ],
  truthCueIds: [
    ...heroLevelUpRecipe.truthCueIds,
    "level-up-cutaway-hall-seal-title",
    "level-up-cutaway-hall-seal-record",
    "level-up-cutaway-hall-seal-provenance",
    "level-up-cutaway-hall-seal-truth",
  ],
  terminalTableau: "equipped-hero-with-exact-earned-hall-seal-and-no-bonus-power",
};

const heroGrowthAllocationRecipe: CutawayRecipeV1 = {
  registryVersion: 1,
  key: "hero-growth-allocation@1",
  packetSchemaVersion: 1,
  phaseOrder: ["deed", "options", "decision", "allocation", "mechanics", "resources", "final"],
  terminalPhase: "final",
  actorRequirements: ["equipped-hero"],
  propRequirements: ["considered-paths", "two-allocation-markers", "mechanical-facts"],
  truthCueIds: [
    "level-up-cutaway-source",
    "level-up-cutaway-threshold",
    "level-up-cutaway-level",
    "level-up-cutaway-selection",
    "level-up-cutaway-candidates",
    "level-up-cutaway-mechanics",
    "level-up-cutaway-tableau",
    "level-up-cutaway-progress",
  ],
  allowedFlavorIds: [],
  durationBudget: { targetMs: 8_500, maximumMs: 11_000, staticHoldMs: 1_200 },
  effectBudget: { movingActors: 1, cameraShots: 2, flavorLayers: 0 },
  terminalTableau: "equipped-hero-with-persisted-growth-allocation-and-resource-truth",
  domEquivalentId: "level-up-cutaway",
  reducedMotion: "complete-static-tableau",
  repetitionFingerprintVersion: null,
  repetitionFingerprintFields: [],
};

const fieldNoteResolutionRecipe: CutawayRecipeV1 = {
  registryVersion: 1,
  key: "field-note-resolution@1",
  packetSchemaVersion: 1,
  phaseOrder: ["observations", "third-mark", "inference", "precedence", "final"],
  terminalPhase: "final",
  actorRequirements: ["observing-hero"],
  propRequirements: ["field-notebook", "verified-species-silhouettes", "cautious-habit-rule"],
  truthCueIds: [
    "field-note-cutaway-observations",
    "field-note-cutaway-current",
    "field-note-cutaway-inference",
    "field-note-cutaway-precedence",
    "field-note-cutaway-notes",
    "field-note-cutaway-progress",
  ],
  allowedFlavorIds: ["ink-trace"],
  durationBudget: { targetMs: 4_800, maximumMs: 6_500, staticHoldMs: 1_200 },
  effectBudget: { movingActors: 0, cameraShots: 1, flavorLayers: 1 },
  terminalTableau: "field-notebook-with-exact-two-to-three-observations-and-live-tell-precedence",
  domEquivalentId: "field-note-cutaway",
  reducedMotion: "complete-static-tableau",
  repetitionFingerprintVersion: 1,
  repetitionFingerprintFields: ["speciesKey"],
};

const fieldNoteResolutionLiveTellRecipe: CutawayRecipeV1 = {
  ...fieldNoteResolutionRecipe,
  key: "field-note-resolution@2",
  packetSchemaVersion: 2,
  propRequirements: [
    "field-notebook",
    "verified-species-silhouette",
    "public-live-signal-glyph",
    "field-habit-glyph",
    "hidden-commitment-lock",
  ],
  truthCueIds: [
    "field-note-cutaway-observations",
    "field-note-cutaway-current",
    "field-note-cutaway-inference",
    "field-note-cutaway-live-tell",
    "field-note-cutaway-precedence",
    "field-note-cutaway-notes",
    "field-note-cutaway-progress",
  ],
  terminalTableau: "public-live-signal-takes-priority-over-field-habit-with-commitment-hidden",
  repetitionFingerprintVersion: 2,
};

const abilityResonanceRecipe: CutawayRecipeV1 = {
  registryVersion: 1,
  key: "ability-resonance@1",
  packetSchemaVersion: 1,
  phaseOrder: ["source", "experience", "resonance", "mastery", "tableau", "final"],
  terminalPhase: "final",
  actorRequirements: ["equipped-hero"],
  propRequirements: ["exact-ability", "mastery-threshold", "effect-glyph", "next-use-timing"],
  truthCueIds: [
    "ability-resonance-cutaway-source",
    "ability-resonance-cutaway-threshold",
    "ability-resonance-cutaway-effect",
    "ability-resonance-cutaway-mastery",
    "ability-resonance-cutaway-next-use",
    "ability-resonance-cutaway-progress",
  ],
  allowedFlavorIds: [],
  durationBudget: { targetMs: 4_800, maximumMs: 6_500, staticHoldMs: 1_200 },
  effectBudget: { movingActors: 1, cameraShots: 2, flavorLayers: 0 },
  terminalTableau: "equipped-hero-with-exact-level-twenty-ability-and-next-use-truth",
  domEquivalentId: "ability-resonance-cutaway",
  reducedMotion: "complete-static-tableau",
  repetitionFingerprintVersion: null,
  repetitionFingerprintFields: [],
};

const weaponMemoryRecipe: CutawayRecipeV1 = {
  registryVersion: 1,
  key: "weapon-memory@1",
  packetSchemaVersion: 1,
  phaseOrder: ["name", "first-use", "road", "familiar-form", "final-strike", "memory", "final"],
  terminalPhase: "final",
  actorRequirements: ["equipped-hero"],
  propRequirements: ["mastered-start-bound-weapon", "forty-five-receipt-marks", "familiar-form-glyph"],
  truthCueIds: [
    "weapon-memory-cutaway-name",
    "weapon-memory-cutaway-first",
    "weapon-memory-cutaway-road",
    "weapon-memory-cutaway-strongest",
    "weapon-memory-cutaway-form",
    "weapon-memory-cutaway-final",
    "weapon-memory-cutaway-progress",
  ],
  allowedFlavorIds: ["receipt-arc", "form-glyph"],
  durationBudget: { targetMs: 8_500, maximumMs: 11_000, staticHoldMs: 1_200 },
  effectBudget: { movingActors: 1, cameraShots: 3, flavorLayers: 2 },
  terminalTableau: "hero-holds-mastered-weapon-with-forty-five-exact-receipt-marks",
  domEquivalentId: "weapon-memory-cutaway",
  reducedMotion: "complete-static-tableau",
  repetitionFingerprintVersion: null,
  repetitionFingerprintFields: [],
};

const battleSpoilsRecipe: CutawayRecipeV1 = {
  registryVersion: 1,
  key: "battle-spoils@1",
  packetSchemaVersion: 1,
  phaseOrder: ["found", "compare", "exchange", "consequence", "final"],
  terminalPhase: "final",
  actorRequirements: ["equipped-hero"],
  propRequirements: ["old-equipment-or-empty-slot", "new-combat-loot", "derived-stat-ledger"],
  truthCueIds: [
    "battle-spoils-cutaway-found",
    "battle-spoils-cutaway-old",
    "battle-spoils-cutaway-new",
    "battle-spoils-cutaway-stats",
    "battle-spoils-cutaway-resources",
    "battle-spoils-cutaway-continuity",
    "battle-spoils-cutaway-progress",
  ],
  allowedFlavorIds: ["equipment-exchange-arrow"],
  durationBudget: { targetMs: 5_200, maximumMs: 7_000, staticHoldMs: 1_200 },
  effectBudget: { movingActors: 1, cameraShots: 2, flavorLayers: 1 },
  terminalTableau: "hero-wears-new-loot-beside-exact-old-to-new-derived-comparison",
  domEquivalentId: "battle-spoils-cutaway",
  reducedMotion: "complete-static-tableau",
  repetitionFingerprintVersion: null,
  repetitionFingerprintFields: [],
};

const townItineraryRecipe: CutawayRecipeV1 = {
  registryVersion: 1,
  key: "town-itinerary@1",
  packetSchemaVersion: 1,
  phaseOrder: ["arrival", "district", "route", "encounter", "consequence"],
  terminalPhase: "consequence",
  actorRequirements: ["equipped-hero", "established-resident"],
  propRequirements: ["real-district", "real-route-buildings", "resident-home-building"],
  truthCueIds: [
    "town-itinerary-cutaway-arrival",
    "town-itinerary-cutaway-district",
    "town-itinerary-cutaway-route",
    "town-itinerary-cutaway-resident",
    "town-itinerary-cutaway-consequence",
  ],
  allowedFlavorIds: [],
  durationBudget: { targetMs: 6_400, maximumMs: 8_000, staticHoldMs: 1_200 },
  effectBudget: { movingActors: 2, cameraShots: 2, flavorLayers: 0 },
  terminalTableau: "equipped-hero-meets-established-resident-at-exact-home-building",
  domEquivalentId: "town-itinerary-cutaway",
  reducedMotion: "complete-static-tableau",
  repetitionFingerprintVersion: null,
  repetitionFingerprintFields: [],
};

const elsewhereLoafRecipe: CutawayRecipeV1 = {
  registryVersion: 1, key: "elsewhere-loaf@1", packetSchemaVersion: 1,
  phaseOrder: ["elsewhere", "final"], terminalPhase: "final",
  actorRequirements: ["actual-former-baker"], propRequirements: ["admitted-inn-oven", "committed-dough-or-product"],
  truthCueIds: ["elsewhere-loaf-actor", "elsewhere-loaf-line", "elsewhere-loaf-supply"], allowedFlavorIds: [],
  durationBudget: { targetMs: 6_400, maximumMs: 8_000, staticHoldMs: 1_200 },
  effectBudget: { movingActors: 1, cameraShots: 1, flavorLayers: 0 },
  terminalTableau: "one-baker-at-admitted-worksite-with-committed-product",
  domEquivalentId: "elsewhere-loaf-cutaway", reducedMotion: "complete-static-tableau",
  repetitionFingerprintVersion: null, repetitionFingerprintFields: [],
};

export const cutawayRegistry = createCutawayRegistry([
  trapRecipe,
  farewellRecipe,
  heroLevelUpRecipe,
  heroLevelUpChampionRecipe,
  heroGrowthAllocationRecipe,
  fieldNoteResolutionRecipe,
  fieldNoteResolutionLiveTellRecipe,
  abilityResonanceRecipe,
  weaponMemoryRecipe,
  battleSpoilsRecipe,
  townItineraryRecipe,
  elsewhereLoafRecipe,
]);

function validPacketEnvelope(packet: CutawayPacketEnvelope): boolean {
  return Number.isSafeInteger(packet.schemaVersion)
    && packet.schemaVersion > 0
    && typeof packet.eventId === "string"
    && packet.eventId.length > 0
    && Number.isSafeInteger(packet.tick)
    && packet.tick >= 0;
}

function validProductionPacket(recipeKey: string, packet: CutawayPacketEnvelope): boolean {
  if (recipeKey === "elsewhere-loaf@1") return isElsewhereLoafPacket(packet);
  if (recipeKey === "trap-resolution@1") return isTrapResolutionPacket(packet);
  if (recipeKey === "companion-farewell@1") return isCompanionFarewellPacket(packet);
  if (recipeKey === "hero-level-up@1") return isHeroLevelUpPacketV1(packet);
  if (recipeKey === "hero-level-up@2") return isHeroLevelUpPacketV2(packet);
  if (recipeKey === "hero-growth-allocation@1") return isHeroGrowthAllocationPacketV1(packet);
  if (recipeKey === "field-note-resolution@1") return isFieldNoteResolutionPacketV1(packet);
  if (recipeKey === "field-note-resolution@2") return isFieldNoteResolutionPacketV2(packet);
  if (recipeKey === "ability-resonance@1") return isAbilityResonancePacketV1(packet);
  if (recipeKey === "weapon-memory@1") return isWeaponMemoryCeremonyPacketV1(packet);
  if (recipeKey === "battle-spoils@1") return isBattleSpoilsComparisonPacketV1(packet);
  if (recipeKey === "town-itinerary@1") return isTownItineraryPacketV1(packet);
  return true;
}

function validStaticEnvelope(envelope: CutawayStaticEnvelopeV1): boolean {
  const text = [envelope.location, envelope.headline, envelope.action, envelope.consequence];
  return envelope.schemaVersion === 1
    && typeof envelope.eventId === "string"
    && envelope.eventId.length > 0
    && Number.isSafeInteger(envelope.tick)
    && envelope.tick >= 0
    && text.every((value) => typeof value === "string" && value.length > 0 && value.length <= 1_000);
}

function staticEnvelopeFromSource(source: ChronicleEntry): CutawayStaticEnvelopeV1 {
  return Object.freeze({
    schemaVersion: 1,
    eventId: source.id,
    tick: source.tick,
    location: source.location,
    headline: source.headline,
    action: source.action,
    consequence: source.consequence,
  });
}

export function createCutawayCandidate<
  Key extends string,
  Packet extends CutawayPacketEnvelope,
>(
  recipeKey: Key,
  packet: Packet,
  staticEnvelope: CutawayStaticEnvelopeV1,
): CutawayCandidate<Key, Packet> {
  const ownedPacket = freezeCopy(packet);
  const ownedStaticEnvelope = freezeCopy(staticEnvelope);
  return Object.freeze({
    registryVersion: cutawayRegistryVersion,
    recipeKey,
    eventId: ownedPacket.eventId,
    packet: ownedPacket,
    staticEnvelope: ownedStaticEnvelope,
  });
}

export function resolveCutawayCandidate(
  registry: CutawayRegistryV1,
  candidate: AnyCutawayCandidate,
): CutawayResolution {
  const staticEnvelope = validStaticEnvelope(candidate.staticEnvelope)
    && candidate.staticEnvelope.eventId === candidate.eventId
    && candidate.staticEnvelope.tick === candidate.packet.tick
    ? candidate.staticEnvelope
    : null;
  if (candidate.registryVersion !== registry.schemaVersion) {
    return Object.freeze({ mode: "static-chronicle", reason: "unknown-registry-version", recipe: null, staticEnvelope });
  }
  const recipe = registry.recipes.find((entry) => entry.key === candidate.recipeKey) ?? null;
  if (recipe === null) {
    return Object.freeze({ mode: "static-chronicle", reason: "unknown-recipe", recipe: null, staticEnvelope });
  }
  if (!validPacketEnvelope(candidate.packet)
    || candidate.eventId !== candidate.packet.eventId
    || staticEnvelope === null) {
    return Object.freeze({ mode: "static-chronicle", reason: "invalid-packet-envelope", recipe, staticEnvelope });
  }
  const extendedTrap = recipe.key === "trap-resolution@1" && recipe.packetSchemaVersion === 1
    && (candidate.packet.schemaVersion === 2 || candidate.packet.schemaVersion === 3)
    && isTrapResolutionPacket(candidate.packet);
  if (candidate.packet.schemaVersion !== recipe.packetSchemaVersion && !extendedTrap) {
    return Object.freeze({ mode: "static-chronicle", reason: "packet-version-mismatch", recipe, staticEnvelope });
  }
  if (!validProductionPacket(candidate.recipeKey, candidate.packet)) {
    return Object.freeze({ mode: "static-chronicle", reason: "invalid-packet-envelope", recipe, staticEnvelope });
  }
  return Object.freeze({ mode: "animate", reason: "registered", recipe, staticEnvelope });
}

function fingerprintValue(value: unknown): string | null {
  if (typeof value === "string") return JSON.stringify(value);
  if (typeof value === "boolean" || typeof value === "number") return String(value);
  return null;
}

/** A semantic fatigue key; event/tick identity remains separate and is never included. */
export function cutawayRepetitionFingerprint(
  registry: CutawayRegistryV1,
  candidate: AnyCutawayCandidate,
): string | null {
  const resolution = resolveCutawayCandidate(registry, candidate);
  const recipe = resolution.mode === "animate" ? resolution.recipe : null;
  if (recipe?.repetitionFingerprintVersion === null || recipe === null) return null;
  const packet = candidate.packet as unknown as Record<string, unknown>;
  const fields: string[] = [];
  for (const field of recipe.repetitionFingerprintFields) {
    const value = fingerprintValue(packet[field]);
    if (value === null) return null;
    fields.push(`${field}=${value}`);
  }
  return `${recipe.key}|v${recipe.repetitionFingerprintVersion}|${fields.join("|")}`;
}

export function createCutawayQueue(): CutawayQueue {
  return Object.freeze({ active: null, pending: null });
}

function candidateIdentity(candidate: AnyCutawayCandidate): string {
  return `${candidate.recipeKey}:${candidate.eventId}`;
}

export function offerCutaway(
  registry: CutawayRegistryV1,
  queue: CutawayQueue,
  candidate: AnyCutawayCandidate,
): CutawayQueueResult {
  const resolution = resolveCutawayCandidate(registry, candidate);
  if (resolution.mode !== "animate") {
    return Object.freeze({ queue, action: "fallback", resolution });
  }
  const identity = candidateIdentity(candidate);
  if ((queue.active !== null && candidateIdentity(queue.active) === identity)
    || (queue.pending !== null && candidateIdentity(queue.pending) === identity)) {
    return Object.freeze({ queue, action: "deduplicated", resolution });
  }
  if (queue.active === null) {
    return Object.freeze({
      queue: Object.freeze({ active: candidate, pending: null }),
      action: "start",
      resolution,
    });
  }
  if (queue.pending === null) {
    return Object.freeze({
      queue: Object.freeze({ active: queue.active, pending: candidate }),
      action: "queued",
      resolution,
    });
  }
  return Object.freeze({ queue, action: "dropped", resolution });
}

export function completeCutaway(queue: CutawayQueue): CutawayQueue {
  return Object.freeze({ active: queue.pending, pending: null });
}

export function discardPendingCutaway(queue: CutawayQueue): CutawayQueue {
  if (queue.pending === null) return queue;
  return Object.freeze({ active: queue.active, pending: null });
}

export function projectCutawayCandidates(
  before: WorldState,
  after: WorldState,
  source: ChronicleEntry,
): readonly ProductionCutawayCandidate[] {
  const trap = projectTrapResolution(before, after, source);
  const farewell = projectCompanionFarewell(before, after, source);
  const growth = projectHeroGrowthAllocation(before, after, source);
  const championLevelUp = growth === null ? projectHeroLevelUpPacketV2(before, after, source) : null;
  const levelUp = growth === null && championLevelUp === null
    ? projectHeroLevelUp(before, after, source)
    : null;
  const fieldNoteResolutionLiveTell = projectFieldNoteResolutionPacketV2(before, after, source);
  const fieldNoteResolution = fieldNoteResolutionLiveTell === null
    ? projectFieldNoteResolution(before, after, source)
    : null;
  const abilityResonance = projectAbilityResonance(before, after, source);
  const weaponMemory = projectWeaponMemoryCeremony(before, after, source);
  const battleSpoils = projectBattleSpoilsComparison(before, after, source);
  const townItinerary = projectTownItinerary(before, after, source);
  if (trap !== null && farewell !== null) return Object.freeze([]);
  const staticEnvelope = staticEnvelopeFromSource(source);
  const candidates: ProductionCutawayCandidate[] = [];
  if (trap !== null) candidates.push(createCutawayCandidate("trap-resolution@1", trap, staticEnvelope));
  else if (farewell !== null) candidates.push(createCutawayCandidate("companion-farewell@1", farewell, staticEnvelope));
  if (levelUp !== null) candidates.push(createCutawayCandidate("hero-level-up@1", levelUp, staticEnvelope));
  if (championLevelUp !== null) candidates.push(createCutawayCandidate("hero-level-up@2", championLevelUp, staticEnvelope));
  if (growth !== null) candidates.push(createCutawayCandidate("hero-growth-allocation@1", growth, staticEnvelope));
  if (fieldNoteResolution !== null) candidates.push(createCutawayCandidate("field-note-resolution@1", fieldNoteResolution, staticEnvelope));
  if (fieldNoteResolutionLiveTell !== null) candidates.push(createCutawayCandidate("field-note-resolution@2", fieldNoteResolutionLiveTell, staticEnvelope));
  if (abilityResonance !== null) candidates.push(createCutawayCandidate("ability-resonance@1", abilityResonance, staticEnvelope));
  if (weaponMemory !== null) candidates.push(createCutawayCandidate("weapon-memory@1", weaponMemory, staticEnvelope));
  if (battleSpoils !== null) candidates.push(createCutawayCandidate("battle-spoils@1", battleSpoils, staticEnvelope));
  if (townItinerary !== null) candidates.push(createCutawayCandidate("town-itinerary@1", townItinerary, staticEnvelope));
  const elsewhere = projectElsewhereLoafTransition(before, after, source);
  if (elsewhere !== null) candidates.push(createCutawayCandidate("elsewhere-loaf@1", elsewhere, {
    schemaVersion: 1, eventId: elsewhere.eventId, tick: elsewhere.tick,
    location: elsewhere.locationName, headline: `Elsewhere: ${elsewhere.companionName}`,
    action: elsewhere.line, consequence: "An independent inn-supplied trial; no hero reward or knowledge.",
  }));
  return Object.freeze(candidates);
}
